<template>
  <div class="modern-form-container">
    <!-- Main Content Card -->
    <div class="form-card">
      <div class="card-header">
        <div class="header-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <h2 class="form-title">
          {{ t('admin-ui.personal_information_form_personal_information') }}
        </h2>
        <p class="form-subtitle">
          {{ t('admin-ui.personal_information_form_explanation_part_1') }}
        </p>
      </div>

      <div class="card-body">
        <hit-form
          name="contactInformationForm"
          :value="contactInformationData"
          :validations="validations"
          :force-cancel-button-hidden="true"
          :force-save-button-hidden="true"
        >
          <template #default="{formData, validationState}">
            <div class="form-grid">
              <!-- Email (read-only) -->
              <div class="input-group full-width">
                <hit-input
                  name="email"
                  :value="userRetrievedData.email"
                  :label="t('admin-ui.email')"
                  :disabled="true"
                />
                <div class="input-status confirmed">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  <span>{{ t('admin-ui.personal-email-confirmed') }}</span>
                </div>
              </div>

              <!-- First Name -->
              <div class="input-group">
                <hit-input
                  name="firstName"
                  :disabled="userRetrievedData.first_name"
                  :value="formData.firstName"
                  :validation-state="validationState.firstName"
                  :label="t('admin-ui.first_name')"
                  @input="onChangeFirstName"
                />
              </div>

              <!-- Last Name -->
              <div class="input-group">
                <hit-input
                  name="lastName"
                  :disabled="userRetrievedData.last_name"
                  :value="formData.lastName"
                  :validation-state="validationState.lastName"
                  :label="t('admin-ui.last_name')"
                  @input="onChangeLastName"
                />
              </div>

              <!-- Password -->
              <div v-if="!userRetrievedData.hide_password" class="input-group">
                <hit-input
                  name="password"
                  :type="inputPasswordType"
                  :show-toggle-password="true"
                  :value="formData.password"
                  :validation-state="validationState.password"
                  :label="t('admin-ui.password')"
                  @password-visibility-change="switchPasswordVisibility"
                  @input="onChangePassword"
                />
                <div class="password-strength">
                  <div
                    class="strength-bar"
                    :class="getPasswordStrength(formData.password)"
                  />
                  <span class="strength-text">{{
                    getPasswordStrengthText(formData.password)
                  }}</span>
                </div>
              </div>

              <!-- Confirm Password -->
              <div v-if="!userRetrievedData.hide_password" class="input-group">
                <hit-input
                  name="confirmPassword"
                  :type="inputConfirmPasswordType"
                  :show-toggle-password="true"
                  :value="formData.confirmPassword"
                  :label="t('admin-ui.confirm_password')"
                  :validation-state="validationState.confirmPassword"
                  @input="onChangeConfirmPassword"
                  @password-visibility-change="switchConfirmPasswordVisibility"
                />
              </div>
            </div>
          </template>
        </hit-form>
      </div>
    </div>

    <!-- Banner -->
    <div v-if="showBanner" class="banner-container">
      <hit-banner :show-animation="showAnimationBanner" @close="closeBanner()">
        <template #dynamic-content>
          <h6>{{ textBanner }}</h6>
        </template>
      </hit-banner>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {
  HitButton,
  HitForm,
  HitGroupInput,
  HitInput,
  HitInputCheckbox,
  HitPanel,
  HitSelect,
} from '@hit/components/src';
import {useI18n} from 'vue-i18n';
import {required, sameAs} from '@vuelidate/validators';
import TopBarNavigation from '../TopBarNavigation.vue';
import HitBanner from '@hit/components/src/components/banner/HitBanner.vue';

export default defineComponent({
  name: 'ContactInformation',
  components: {
    HitInputCheckbox,
    TopBarNavigation,
    HitPanel,
    HitSelect,
    HitInput,
    HitGroupInput,
    HitButton,
    HitForm,
    HitBanner,
  },
  props: {
    showMobileView: {
      type: Boolean,
      default: false,
    },
    showBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
    textBanner: {
      type: String,
      default: '',
      required: true,
    },
    showAnimationBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
    errorFetchApi: {
      type: Boolean,
      default: false,
      required: false,
    },
    userRetrievedData: {
      type: Object,
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
    triggerValidation: {
      type: Number,
      default: 0,
    },
  },
  emits: ['nextClick', 'close', 'validationResult'],
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      contactInformationData: {
        email: null as string | null,
        firstName: this.userData.firstName,
        lastName: this.userData.lastName,
        password: this.userData.password,
        confirmPassword: this.userData.confirmPassword,
        referralCode: null as string | null,
      },
      isErrorWithMailPart: true,
      errorMessageEmail: '',
      isErrorWithPasswordPart: true,
      errorMessagePassword: '',
      isActive: false,
      valueAllowNextClick: false,
      emailCompleted: false,
      mobileView: false,
      showErrorEmailAddress: false,
      inputPasswordType: 'password',
      inputConfirmPasswordType: 'password',
    };
  },
  computed: {
    validations() {
      return {
        firstName: {required},
        lastName: {required},
        password: {required},
        confirmPassword: {
          required,
          sameAsPassword: sameAs(this.contactInformationData.password),
        },
      };
    },
  },
  watch: {
    userRetrievedData(newVal) {
      if (newVal) {
        this.updateContactInformation();
      }
    },
    triggerValidation(newVal, oldVal) {
      if (newVal > oldVal) {
        this.validateAndContinue();
      }
    },
  },
  mounted() {
    if (this.userRetrievedData) {
      this.updateContactInformation();
    }
    this.allowNextClick();
  },
  methods: {
    closeBanner() {
      this.$emit('close');
    },
    switchPasswordVisibility() {
      this.inputPasswordType =
        this.inputPasswordType === 'password' ? 'text' : 'password';
    },
    switchConfirmPasswordVisibility() {
      this.inputConfirmPasswordType =
        this.inputConfirmPasswordType === 'password' ? 'text' : 'password';
    },

    allowNextClick(): boolean {
      if (this.userRetrievedData && this.userRetrievedData.hide_password) {
        this.valueAllowNextClick = true;
        return true;
      } else if (
        this.contactInformationData.password !== null &&
        this.contactInformationData.password !== '' &&
        this.contactInformationData.confirmPassword !== null &&
        this.contactInformationData.confirmPassword !== '' &&
        this.contactInformationData.lastName !== null &&
        this.contactInformationData.lastName !== '' &&
        this.contactInformationData.firstName !== null &&
        this.contactInformationData.firstName !== '' &&
        this.contactInformationData.password ==
          this.contactInformationData.confirmPassword
      ) {
        this.valueAllowNextClick = true;
        return true;
      } else {
        this.valueAllowNextClick = false;
        return false;
      }
    },
    onChangePassword(value: any) {
      this.contactInformationData.password = value;
      this.allowNextClick();
    },
    onChangeConfirmPassword(value: any) {
      this.contactInformationData.confirmPassword = value;
      this.allowNextClick();
    },
    onChangeFirstName(value: any) {
      this.contactInformationData.firstName = value;
      this.allowNextClick();
    },
    onChangeLastName(value: any) {
      this.contactInformationData.lastName = value;
      this.allowNextClick();
    },
    updateContactInformation() {
      if (this.userRetrievedData?.first_name) {
        this.contactInformationData.firstName =
          this.userRetrievedData.first_name;
      }
      if (this.userRetrievedData?.last_name) {
        this.contactInformationData.lastName = this.userRetrievedData.last_name;
      }

      if (this.userRetrievedData?.referral_code) {
        this.contactInformationData.referralCode =
          this.userRetrievedData.referral_code;
      }
      this.allowNextClick();
    },
    getPasswordStrength(password: string) {
      if (!password) return 'strength-none';

      let score = 0;
      if (password.length >= 8) score++;
      if (/[a-z]/.test(password)) score++;
      if (/[A-Z]/.test(password)) score++;
      if (/[0-9]/.test(password)) score++;
      if (/[^A-Za-z0-9]/.test(password)) score++;

      if (score < 2) return 'strength-weak';
      if (score < 4) return 'strength-medium';
      return 'strength-strong';
    },
    getPasswordStrengthText(password: string) {
      const strength = this.getPasswordStrength(password);
      if (strength === 'strength-weak') {
        return this.t('admin-ui.password-strength-weak');
      } else if (strength === 'strength-medium') {
        return this.t('admin-ui.password-strength-medium');
      } else if (strength === 'strength-strong') {
        return this.t('admin-ui.password-strength-strong');
      }
      return '';
    },
    validateAndContinue() {
      // Check if required fields are filled for normal flow
      if (this.userRetrievedData && this.userRetrievedData.hide_password) {
        // Password not required, check only name fields
        if (
          !this.contactInformationData.firstName ||
          !this.contactInformationData.lastName
        ) {
          let missingFields = [];
          if (!this.contactInformationData.firstName) {
            missingFields.push(this.t('admin-ui.first_name').toLowerCase());
          }
          if (!this.contactInformationData.lastName) {
            missingFields.push(this.t('admin-ui.last_name').toLowerCase());
          }

          this.$emit('validationResult', {
            success: false,
            message: `${this.t(
              'admin-ui.please-fill-your',
            )} ${missingFields.join(` ${this.t('admin-ui.and')} `)}.`,
          });
          return;
        }

        // All required fields filled, proceed
        this.$emit('nextClick', {
          email: this.userRetrievedData.email,
          password: this.contactInformationData.password,
          confirmPassword: this.contactInformationData.confirmPassword,
          lastName: this.contactInformationData.lastName,
          firstName: this.contactInformationData.firstName,
          referralCode: this.contactInformationData.referralCode,
        });
        this.$emit('validationResult', {success: true});
        return;
      }

      // For normal flow, check all required fields including password
      let missingFields = [];

      if (!this.contactInformationData.firstName) {
        missingFields.push(this.t('admin-ui.first_name').toLowerCase());
      }
      if (!this.contactInformationData.lastName) {
        missingFields.push(this.t('admin-ui.last_name').toLowerCase());
      }
      if (!this.contactInformationData.password) {
        missingFields.push(this.t('admin-ui.password').toLowerCase());
      }
      if (!this.contactInformationData.confirmPassword) {
        missingFields.push(this.t('admin-ui.confirm_password').toLowerCase());
      }

      if (missingFields.length > 0) {
        let message;
        if (missingFields.length === 1) {
          message = `${this.t('admin-ui.please-fill-your')} ${
            missingFields[0]
          }.`;
        } else if (missingFields.length === 2) {
          message = `${this.t(
            'admin-ui.please-fill-your',
          )} ${missingFields.join(` ${this.t('admin-ui.and')} `)}.`;
        } else {
          const lastField = missingFields.pop();
          message = `${this.t(
            'admin-ui.please-fill-your',
          )} ${missingFields.join(', ')} ${this.t(
            'admin-ui.and',
          )} ${lastField}.`;
        }

        this.$emit('validationResult', {
          success: false,
          message: message,
        });
        return;
      }

      // Check password match
      if (
        this.contactInformationData.password !==
        this.contactInformationData.confirmPassword
      ) {
        this.$emit('validationResult', {
          success: false,
          message: this.t('admin-ui.passwords-do-not-match'),
        });
        return;
      }

      // All validation passed, emit nextClick to trigger actual form submission
      this.$emit('nextClick', {
        email: this.userRetrievedData.email,
        password: this.contactInformationData.password,
        confirmPassword: this.contactInformationData.confirmPassword,
        lastName: this.contactInformationData.lastName,
        firstName: this.contactInformationData.firstName,
        referralCode: this.contactInformationData.referralCode,
      });
      this.$emit('validationResult', {success: true});
    },
  },
});
</script>

<style scoped>
.modern-form-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 3rem 1rem 1rem 1rem;
  background: #f1f5f9;
  position: relative;
}

.form-card {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 0;
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid #e5e7eb;
}

.card-header {
  background: #0d3749;
  color: white;
  padding: 1.5rem;
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  margin-bottom: 0.75rem;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 600;
  margin: 0 0 0.375rem 0;
  line-height: 1.3;
}

.form-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.card-body {
  padding: 1.5rem;
}

.form-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
}

.input-group {
  position: relative;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.input-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.input-status.confirmed {
  color: #059669;
}

.input-status svg {
  flex-shrink: 0;
}

.password-strength {
  margin-top: 0.5rem;
}

.strength-bar {
  height: 3px;
  border-radius: 1.5px;
  transition: all 0.3s ease;
  margin-bottom: 0.375rem;
  background: #e5e7eb;
}

.strength-bar.strength-weak {
  width: 33%;
  background: #dc2626;
}

.strength-bar.strength-medium {
  width: 66%;
  background: #d97706;
}

.strength-bar.strength-strong {
  width: 100%;
  background: #059669;
}

.strength-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}

.strength-bar.strength-weak + .strength-text {
  color: #dc2626;
}

.strength-bar.strength-medium + .strength-text {
  color: #d97706;
}

.strength-bar.strength-strong + .strength-text {
  color: #059669;
}

.password-match {
  margin-top: 0.5rem;
}

.match-success,
.match-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.match-success {
  color: #059669;
}

.match-error {
  color: #dc2626;
}

.match-success svg,
.match-error svg {
  flex-shrink: 0;
}

.banner-container {
  margin-top: 1rem;
  max-width: 600px;
  width: 100%;
}

/* Responsive Design */
@media (min-width: 768px) {
  .modern-form-container {
    padding: 4rem 1rem 1rem 1rem;
  }

  .form-grid {
    grid-template-columns: 1fr 1fr;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 1rem;
  }

  .card-header {
    padding: 1.75rem;
  }

  .card-body {
    padding: 1.75rem;
  }
}

@media (max-width: 768px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.5rem 1rem 0.25rem 1rem;
    justify-content: center;
  }

  .form-card {
    max-width: 100%;
    border-radius: 0.75rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .form-grid {
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.25rem 0.75rem 0.125rem 0.75rem;
  }

  .form-card {
    border-radius: 0.5rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.125rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }
}
</style>
